import { Component, Input, HostBinding, ContentChild, TemplateRef } from '@angular/core';
import { CardVariant, Size } from '../../../shared/models/design-system.types';

/**
 * HotelCard Card Component
 *
 * A flexible card component with multiple visual variants.
 *
 * Usage:
 * ```html
 * <hc-card variant="raised">
 *   <ng-template #header>Card Header</ng-template>
 *   Card content here
 *   <ng-template #footer>Card Footer</ng-template>
 * </hc-card>
 * ```
 */
@Component({
    selector: 'hc-card',
    templateUrl: './card.component.html',
    styleUrls: ['./card.component.scss'],
    standalone: false
})
export class CardComponent {
  /** Visual variant of the card */
  @Input() variant: CardVariant = 'flat';

  /** Whether the card is clickable */
  @Input() clickable = false;

  /** Whether to show hover effects */
  @Input() hoverable = false;

  /** Padding size */
  @Input() padding: Size | 'none' = 'md';

  /** Whether to remove the border */
  @Input() borderless = false;

  /** Custom CSS class */
  @Input() cssClass = '';

  /** Header template */
  @ContentChild('header') headerTemplate?: TemplateRef<unknown>;

  /** Footer template */
  @ContentChild('footer') footerTemplate?: TemplateRef<unknown>;

  @HostBinding('class') get hostClasses(): string {
    const classes = [
      'hc-card',
      `hc-card--${this.variant}`,
      `hc-card--padding-${this.padding}`,
    ];

    if (this.clickable) classes.push('hc-card--clickable');
    if (this.hoverable) classes.push('hc-card--hoverable');
    if (this.borderless) classes.push('hc-card--borderless');
    if (this.cssClass) classes.push(this.cssClass);

    return classes.join(' ');
  }
}
